import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { ListFilters } from '../../../../interfaces/productDivision.interface';
import { buildHeadCells } from '../../../../utils/methods';
import {
  VALIDATION_FIELD_MAPPING,
  HEAD_CELL_SORT_MAPPING,
  GENERIC_LOOKUP_KEYS,
  STATUS,
} from '../../../../utils/appConstant';
import useSorting from '../../../../hooks/useSorting';
import usePagination from '../../../../hooks/usePagination';
import useFetch from '../../../../hooks/useFetch';
import ProductDivisionService from '../../../../services/productDivision.service';
import CommonService from '../../../../services/common.service';
import { SortOrder } from '../../../../enums';

const ProductDivisionList: React.FC = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>({
    searchText: '',
    status: STATUS.active,
  });

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({ status: '' });

  const headCells: HeadCellType[] = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.name,
      VALIDATION_FIELD_MAPPING.description,
      VALIDATION_FIELD_MAPPING.status,
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'productDivisionList'
  );

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.asc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.name,
  });

  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  const { total, data: divisions, loading } = useFetch({
    service: ProductDivisionService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: HEAD_CELL_SORT_MAPPING.productDivisionList?.[orderBy] || orderBy,
      order,
      searchText: filters.searchText.trim(),
      status: filters.status === STATUS.active ? '1' : filters.status === STATUS.inactive ? '0' : '',
    },
    dependency: [order, orderBy, page, perPage, filters.searchText, filters.status],
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: [],
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status],
    skip: !dropdownSearchData.status,
  });

  const handleFiltersChange = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.name);
    setFilters(newFilters);
  }, []);

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => ({ ...prev, [name]: event.target.value }));
    },
    []
  );

  const headerSection = <TableHeaderSection total={total} />;

  const values: TableContextProvider = useMemo(
    () => ({
      headerSection,
      row: Row,
      data: divisions || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('productDivisions.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, order, orderBy, page, perPage, total, loading, divisions, setPage, setPerPage, setOrder, setOrderBy]
  );

  return (
    <>
      <Header />
      <Filters
        status={dropdownSearchData.status ? status : allStatus}
        allStatus={allStatus}
        dropdownSearchData={dropdownSearchData}
        initialFilters={filters}
        onFilterChange={handleFiltersChange}
        onDropdownSearchChange={handleDropdownSearchInputChange}
      />
      <TableBox tableValues={values} />
    </>
  );
};

export default ProductDivisionList;
